/**
 * 交易所数据采集配置 API 服务
 * 提供采集配置的增删改查接口封装
 *
 * @author page
 * @date 2026-04-24
 */
import {api} from './api';
import type {
    ConfigType,
    ExchangeDataConfig,
    ExchangeDataConfigRequest,
    ExchangeName,
    PageResponse,
    ProductType,
} from '../types/exchangeDataConfig';
import type {ApiResponse} from '../types/common';

/** 分页查询参数 */
export interface ExchangeDataConfigQueryParams {
    exchangeName?: ExchangeName;
    productType?: ProductType;
    configType?: ConfigType;
    enabled?: number;
    symbol?: string;
    page?: number;
    size?: number;
}

export const exchangeDataConfigService = {
    /**
     * 分页查询采集配置（带筛选条件）
     */
    getConfigsPage: (params?: ExchangeDataConfigQueryParams) =>
        api.get<ApiResponse<PageResponse<ExchangeDataConfig>>>('/api/v1/data/exchange-configs', {params: params || {}}),

    /**
     * 获取所有启用的采集配置
     */
    getEnabledConfigs: () =>
        api.get<ApiResponse<ExchangeDataConfig[]>>('/api/v1/data/exchange-configs/enabled'),

    /**
     * 根据ID获取采集配置
     * @param id 配置ID
     */
    getConfigById: (id: number) =>
        api.get<ApiResponse<ExchangeDataConfig>>(`/api/v1/data/exchange-configs/${id}`),

    /**
     * 根据交易所名称获取采集配置列表
     * @param exchangeName 交易所名称
     */
    getByExchange: (exchangeName: string) =>
        api.get<ApiResponse<ExchangeDataConfig[]>>(`/api/v1/data/exchange-configs/exchange/${exchangeName}`),

    /**
     * 根据数据源获取采集配置列表
     * @param dataSource 数据源（live/paper）
     */
    getByDataSource: (dataSource: string) =>
        api.get<ApiResponse<ExchangeDataConfig[]>>(`/api/v1/data/exchange-configs/datasource/${dataSource}`),

    /**
     * 新增采集配置
     * @param data 配置请求
     */
    addConfig: (data: ExchangeDataConfigRequest) =>
        api.post<ApiResponse<ExchangeDataConfig>>('/api/v1/data/exchange-configs', data),

    /**
     * 更新采集配置
     * @param id 配置ID
     * @param data 配置请求
     */
    updateConfig: (id: number, data: ExchangeDataConfigRequest) =>
        api.put<ApiResponse<ExchangeDataConfig>>(`/api/v1/data/exchange-configs/${id}`, data),

    /**
     * 删除采集配置
     * @param id 配置ID
     */
    deleteConfig: (id: number) =>
        api.delete<ApiResponse<void>>(`/api/v1/data/exchange-configs/${id}`),

    /**
     * 更新采集配置状态（启用/禁用）
     * @param id 配置ID
     * @param enabled 状态（0-启用 1-禁用）
     */
    updateConfigStatus: (id: number, enabled: 0 | 1) =>
        api.put<ApiResponse<ExchangeDataConfig>>(`/api/v1/data/exchange-configs/${id}/status`, null, {
            params: {enabled},
        }),

    /**
     * 批量更新采集配置
     * @param data 配置请求列表
     */
    batchUpdateConfigs: (data: ExchangeDataConfigRequest[]) =>
        api.put<ApiResponse<ExchangeDataConfig[]>>('/api/v1/data/exchange-configs/batch', data),

    /**
     * 手动触发 temporary 归集比对
     * @param vendor 供应商，默认 OKX
     * @param instType 合约类型，默认 SWAP
     */
    syncTemporaryConfigs: (vendor = 'OKX', instType = 'SWAP') =>
        api.post<ApiResponse<number>>('/api/v1/data/exchange-configs/temporary/sync', null, {
            params: {vendor, instType},
        }),
};
